import { useCallback } from 'react';
import classNames from 'classnames/bind';
import { area, line } from 'd3-shape';
import { scaleTime, scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { isFinite, round } from 'lodash';

import type { ForecastGraphEnergyContainerProps } from '../components/ForecastGraphEnergyContainer/ForecastGraphEnergyContainer';

import styles from '../components/ForecastGraphEnergyContainer/ForecastGraphEnergyContainer.module.scss';

const cx = classNames.bind(styles);

interface GraphDataPoint {
  hour: number;
  power: number;
}
interface Props extends ForecastGraphEnergyContainerProps {
  graphHeight?: number;
  graphWidth?: number;
}

const useSetupForecastGraphEnergy = ({
  data,
  graphHeight,
  graphWidth,
  numberOfTicks,
  overallMaxEnergy,
}: Props) => {
  const setupForecastGraphEnergy = useCallback(() => {
    if (graphWidth && graphHeight && isFinite(graphHeight) && isFinite(graphWidth)) {
      select(`.swell-energy-graph-ticks`).remove();
      select(`.swell-energy-graph-svg`).remove();

      const graphData: Array<GraphDataPoint> = data.map((dataPoint, index: number) => ({
        ...dataPoint,
        hour: index,
        energy: dataPoint.power,
      }));

      const xPadding = 0;
      const yPadding = 0;

      const x = scaleTime()
        .range([xPadding, graphWidth - xPadding])
        .domain([0, data.length - 1]);

      const y = scaleLinear()
        .range([graphHeight - yPadding, yPadding])
        .domain([0, overallMaxEnergy]);

      const path = line<GraphDataPoint>()
        .x((d) => x(d.hour))
        .y((d) => y(d.power));

      // https://observablehq.com/@d3/area-chart
      const drawArea = area<GraphDataPoint>()
        .x((d) => x(d.hour))
        .y0(y(0)) // Set the bottom y-coordinate to the y-coordinate of the bottom axis
        .y1((d) => y(d.power)); // Set the top y-coordinate to the y-coordinate of the data point

      const canvas = select(`.swell-energy-graph-canvas`);

      if (numberOfTicks > 0) {
        const maxTick = round(overallMaxEnergy);
        const intermediateTicks = [...Array(numberOfTicks - 1)].map((_, index) =>
          round((index + 1) * (maxTick / numberOfTicks), 1),
        );

        const tickData = [...intermediateTicks, maxTick];

        const ticks = canvas.append('div').attr('class', 'swell-energy-graph-ticks');

        ticks
          .selectAll('.swell-energy-graph-tick')
          .data(tickData)
          .enter()
          .append('div')
          .attr('class', cx(['swell-energy-graph-tick', styles.tick]))
          .style('top', (d) => `${y(d)}px`)
          .text((d) => `${d < 10 ? d : round(d)}`);

        ticks
          .selectAll('.swell-energy-graph-dashed-line')
          .data(tickData)
          .enter()
          .append('div')
          .attr('class', cx(['swell-energy-graph-dashed-line', styles.dashedLine]))
          .style('top', (d) => `${y(d)}px`);
      }

      const svg = canvas.append('svg').attr('class', cx(['swell-energy-graph-svg', styles.svg]));

      const energyGroup = svg.append('g').attr('class', 'swell-energy');

      energyGroup
        .append('path')
        .attr('class', styles.visibleArea)
        .attr('d', drawArea(graphData as any));

      energyGroup
        .append('path')
        .attr('class', styles.visibleLine)
        .attr('d', path(graphData as any));
    }
  }, [data, graphHeight, graphWidth, numberOfTicks, overallMaxEnergy]);

  return { setupForecastGraphEnergy };
};

export default useSetupForecastGraphEnergy;
